import { type JSX } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
} from '../../components';
import {
  base64UrlToUint8Array,
  decryptWithKeyBytes,
} from '../../crypto';
import { getSecretHint, retrieveSecret, reportAbuse } from '../../api';

interface RetrieveSecretPageProps {
  secretId: string;
}

type PageState = 'loading' | 'error' | 'ready' | 'retrieved' | 'invalid-key';

export function RetrieveSecretPage({ secretId }: RetrieveSecretPageProps): JSX.Element {
  const [state, setState] = useState<PageState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [decryptedSecret, setDecryptedSecret] = useState<string | null>(null);
  const [keyBase64, setKeyBase64] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  useEffect(() => {
    const initializeSecret = async (): Promise<void> => {
      // Extract key from URL fragment
      const fragment = window.location.hash.slice(1);
      if (!fragment) {
        setState('invalid-key');
        setError('No decryption key found. The URL must contain the key in the fragment.');
        return;
      }

      setKeyBase64(fragment);

      // Fetch hint before user clicks retrieve
      try {
        const hintResponse = await getSecretHint(secretId);

        // Decrypt hint if provided
        if (hintResponse.hint) {
          const keyBytes = base64UrlToUint8Array(fragment);
          const decryptedHint = await decryptWithKeyBytes(hintResponse.hint, keyBytes);
          setHint(decryptedHint);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load hint';
        setError(message);
        setState('error');
        return;
      }

      setState('ready');
    };

    initializeSecret();
  }, [secretId]);

  // Warn user before leaving if secret is displayed but not copied
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string => {
      // Only warn if secret is retrieved but not copied
      if (state === 'retrieved' && !copied) {
        e.preventDefault();
        return '';
      }
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state, copied]);

  const handleRetrieve = async (): Promise<void> => {
    setState('loading');
    setError(null);

    try {
      if (!keyBase64) {
        throw new Error('Decryption key not found');
      }

      // Retrieve encrypted secret from server (deletes it)
      const result = await retrieveSecret(secretId);

      // Decrypt using key from URL fragment (URL-safe base64)
      const keyBytes = base64UrlToUint8Array(keyBase64);
      const plaintext = await decryptWithKeyBytes(result.secret, keyBytes);

      setDecryptedSecret(plaintext);
      setState('retrieved');

      // Clear the secret from browser history by replacing the URL
      window.history.replaceState(null, '', window.location.pathname);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setState('error');
      setError(message);
    }
  };

  const handleReportAbuse = async (): Promise<void> => {
    if (!reportReason.trim()) {
      setReportError('Please provide a reason for reporting');
      return;
    }

    try {
      await reportAbuse(
        secretId,
        reportReason,
        reportDetails.trim() ? reportDetails : undefined
      );
      setReportSubmitted(true);
      setReportError(null);
      setTimeout(() => {
        setShowReportDialog(false);
        setReportSubmitted(false);
        setReportReason('');
        setReportDetails('');
      }, 2000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit report';
      setReportError(message);
    }
  };

  if (state === 'invalid-key') {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Invalid URL</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert type="error" title="Error">
              {error}
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Loading secret...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Error Retrieving Secret</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert type="error" title="Error">
              {error}
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'retrieved' && decryptedSecret !== null) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Secret Retrieved</CardTitle>
            <CardDescription>
              This secret has been automatically deleted from the server
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert type="warning" title="Important" className="mb-6">
              <ul className="list-disc list-inside space-y-1">
                <li>This secret has been permanently deleted from the server</li>
                <li>
                  The key in your browser history can decrypt this secret — consider clearing
                  history
                </li>
                <li>Change any passwords in this secret immediately on first login</li>
              </ul>
            </Alert>

            {hint && (
              <Alert type="info" title="Hint" className="mb-6">
                {hint}
              </Alert>
            )}

            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-4 mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Secret:</p>
              <pre className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded p-3 overflow-x-auto whitespace-pre-wrap break-words max-h-64 overflow-y-auto text-sm font-mono text-gray-900 dark:text-gray-100">
                {decryptedSecret}
              </pre>
            </div>

            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  navigator.clipboard.writeText(decryptedSecret);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? '✓ Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>

            {/* Report Abuse Section */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              {!showReportDialog && !reportSubmitted && (
                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Received an inappropriate or abusive secret?
                  </p>
                  <Button
                    className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                    size="sm"
                    onClick={() => setShowReportDialog(true)}
                  >
                    🚩 Report Abuse
                  </Button>
                </div>
              )}

              {showReportDialog && !reportSubmitted && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reason for reporting *
                    </label>
                    <select
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      value={reportReason}
                      onChange={(e) => setReportReason(e.target.value)}
                    >
                      <option value="">Select a reason...</option>
                      <option value="spam">Spam or advertising</option>
                      <option value="harassment">Harassment or bullying</option>
                      <option value="illegal">Illegal content</option>
                      <option value="malware">Malware or phishing</option>
                      <option value="inappropriate">Inappropriate content</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Additional details (optional)
                    </label>
                    <textarea
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      rows={3}
                      maxLength={1000}
                      placeholder="Provide additional context if needed..."
                      value={reportDetails}
                      onChange={(e) => setReportDetails(e.target.value)}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {reportDetails.length}/1000 characters
                    </p>
                  </div>

                  {reportError && (
                    <Alert type="error" title="Error">
                      {reportError}
                    </Alert>
                  )}

                  <div className="flex gap-2">
                    <Button onClick={handleReportAbuse}>Submit Report</Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowReportDialog(false);
                        setReportError(null);
                        setReportReason('');
                        setReportDetails('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {reportSubmitted && (
                <Alert type="success" title="Report Submitted">
                  Your abuse report has been submitted successfully. Thank you for helping
                  keep this service safe.
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // State: ready (waiting for user to click retrieve)
  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Retrieve Your Secret</CardTitle>
          <CardDescription>
            The encrypted secret is stored on the server. Click below to retrieve and decrypt it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert type="warning" title="Security Notice" className="mb-6">
            <ul className="list-disc list-inside space-y-1">
              <li>This service cannot protect against TLS interception (corporate "SSL inspection")</li>
              <li>Browser history will contain the decryption key</li>
              <li>The secret will be permanently deleted after retrieval</li>
              <li>Change any passwords immediately on first login</li>
            </ul>
          </Alert>

          {hint && (
            <Alert type="info" title="Hint" className="mb-6">
              {hint}
            </Alert>
          )}

          <div className="flex gap-2 justify-center pt-4">
            <Button size="lg" onClick={handleRetrieve}>
              Retrieve Secret
            </Button>
          </div>

          {/* Report Abuse Section */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            {!showReportDialog && !reportSubmitted && (
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Received an inappropriate or abusive secret?
                </p>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                  size="sm"
                  onClick={() => setShowReportDialog(true)}
                >
                  🚩 Report Abuse
                </Button>
              </div>
            )}

            {showReportDialog && !reportSubmitted && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Reason for reporting *
                  </label>
                  <select
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                  >
                    <option value="">Select a reason...</option>
                    <option value="spam">Spam or advertising</option>
                    <option value="harassment">Harassment or bullying</option>
                    <option value="illegal">Illegal content</option>
                    <option value="malware">Malware or phishing</option>
                    <option value="inappropriate">Inappropriate content</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Additional details (optional)
                  </label>
                  <textarea
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={3}
                    maxLength={1000}
                    placeholder="Provide additional context if needed..."
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {reportDetails.length}/1000 characters
                  </p>
                </div>

                {reportError && (
                  <Alert type="error" title="Error">
                    {reportError}
                  </Alert>
                )}

                <div className="flex gap-2">
                  <Button onClick={handleReportAbuse}>Submit Report</Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowReportDialog(false);
                      setReportError(null);
                      setReportReason('');
                      setReportDetails('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {reportSubmitted && (
              <Alert type="success" title="Report Submitted">
                Your abuse report has been submitted successfully. Thank you for helping
                keep this service safe.
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
